import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AlertTriangle, Lock } from "lucide-react"
import { useState } from "react"

import { api, ErrorApi } from "@/api/cliente"

/**
 * Cierre financiero del expediente.
 *
 * ⚠ **Ofrece cerrar sólo cuando el servidor dice que se puede.** Las tres reglas de
 * qué bloquea —un IMPUESTO sin pagar, un GASTO en BORRADOR, y un SERVICIO en
 * BORRADOR que **no** bloquea porque se convierte en CxC— viven en
 * `maquina_estados.py`. Aquí no se calcula ninguna: se pide a
 * `/bloqueos-de-cierre` y se pinta lo que conteste. Duplicarlas en el navegador
 * garantiza que las dos versiones se separen, y la que se quede vieja es la que
 * decide si alguien pulsa el botón.
 *
 * ⚠ **Dos pasos, y no por ceremonia.** Cerrar saca el expediente de todas las
 * bandejas: lo que quede dentro deja de mirarlo nadie. Además convierte cada
 * servicio en borrador en una cuenta por cobrar al cliente, que es dinero que se le
 * empieza a reclamar. Un botón de un solo clic para eso es un botón que se pulsa sin
 * querer.
 *
 * ⚠ **Sin actualización optimista**, como el avance de estatus: el servidor puede
 * rechazar el cierre, y pintar un éxito que va a negarse es el peor caso posible.
 */

type Bloqueos = {
  puede_cerrar: boolean
  pendientes: Record<string, string[]>
}

/** Qué significa cada categoría bloqueada, en la lengua del tramitador. */
const EXPLICACION: Record<string, string> = {
  IMPUESTO: "Tributos sin pagar. Se le deben al fisco.",
  GASTO: "Gastos en borrador. Nadie terminó de registrarlos.",
  SERVICIO: "Servicios en borrador.",
}

export function DialogoCerrarExpediente({ expedienteId }: { expedienteId: number }) {
  const clienteQuery = useQueryClient()
  const [confirmando, setConfirmando] = useState(false)

  const bloqueos = useQuery({
    queryKey: ["bloqueos-de-cierre", expedienteId],
    queryFn: () => api.get<Bloqueos>(`/api/v1/expedientes/${expedienteId}/bloqueos-de-cierre`),
  })

  const cerrar = useMutation({
    mutationFn: () => api.post(`/api/v1/expedientes/${expedienteId}/cerrar`),
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: ["expediente", expedienteId] })
      clienteQuery.invalidateQueries({ queryKey: ["bloqueos-de-cierre", expedienteId] })
      clienteQuery.invalidateQueries({ queryKey: ["rubros-expediente", expedienteId] })
      // Los servicios en borrador acaban de convertirse en cuentas por cobrar.
      clienteQuery.invalidateQueries({ queryKey: ["cxc"] })
      clienteQuery.invalidateQueries({ queryKey: ["expedientes"] })
      setConfirmando(false)
    },
  })

  if (bloqueos.isPending) {
    return (
      <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
        Comprobando si se puede cerrar…
      </p>
    )
  }

  if (bloqueos.isError) {
    // Sin saber qué bloquea no se ofrece cerrar. Ofrecerlo a ciegas es ofrecer un 409.
    return (
      <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
        No se pudo comprobar el cierre.
      </p>
    )
  }

  const categorias = Object.entries(bloqueos.data.pendientes)

  if (categorias.length > 0) {
    return (
      <div>
        <p className="flex items-center gap-2 text-sm font-medium">
          <AlertTriangle size={16} aria-hidden={true} style={{ color: "var(--warning)" }} />
          No se puede cerrar todavía
        </p>
        <ul className="mt-2 space-y-2 text-sm">
          {categorias.map(([categoria, conceptos]) => (
            <li key={categoria}>
              <span className="font-medium">{categoria}</span>
              <span style={{ color: "var(--fg-muted)" }}>
                {" — "}
                {EXPLICACION[categoria] ?? "Pendiente de resolver."}
              </span>
              <ul className="mt-1 ml-4 list-disc" style={{ color: "var(--fg-muted)" }}>
                {conceptos.map((concepto) => (
                  <li key={concepto} data-testid={`bloqueo-${categoria}`}>
                    {concepto}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </div>
    )
  }

  return (
    <div>
      {confirmando ? (
        <div>
          <p className="text-sm">
            Cerrar saca el expediente de todas las bandejas, y cada servicio en borrador pasa a ser
            una cuenta por cobrar al cliente.
          </p>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={() => setConfirmando(false)}
              className="rounded-[var(--radius-control)] border px-3 py-1.5 text-sm"
              style={{ borderColor: "var(--border)" }}
            >
              Cancelar
            </button>
            <button
              type="button"
              data-testid="confirmar-cierre"
              disabled={cerrar.isPending}
              onClick={() => cerrar.mutate()}
              className="rounded-[var(--radius-control)] px-3 py-1.5 text-sm disabled:opacity-50"
              style={{ background: "var(--destructive)", color: "var(--primary-fg)" }}
            >
              {cerrar.isPending ? "Cerrando…" : "Sí, cerrar el expediente"}
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          data-testid="abrir-cierre"
          onClick={() => setConfirmando(true)}
          className="flex items-center gap-2 rounded-[var(--radius-control)] border px-3 py-1.5 text-sm"
          style={{ borderColor: "var(--border)" }}
        >
          <Lock size={16} aria-hidden={true} />
          Cerrar expediente
        </button>
      )}

      {/*
        El 409 del servidor enumera lo pendiente en `details.pendientes`. Puede
        llegar aunque la consulta dijera que se podía: entre una cosa y otra alguien
        pudo añadir un rubro. Lo que manda es esto.
      */}
      {cerrar.isError && (
        <p role="alert" className="mt-3 text-sm" style={{ color: "var(--destructive)" }}>
          {cerrar.error instanceof ErrorApi
            ? cerrar.error.message
            : "No se pudo cerrar el expediente."}
        </p>
      )}
    </div>
  )
}
